import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function TestReviewDialog({ resultId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCocktail, setExpandedCocktail] = useState(null);

  useEffect(() => {
    if (!open || !resultId) return;
    setLoading(true);
    setData(null);
    setExpandedCocktail(null);
    axios.get(`${API}/test-results/${resultId}/review`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, resultId]);

  const result = data?.result;
  const review = data?.review || [];

  const getScoreColor = (pct) => {
    if (pct >= 80) return "text-green-500";
    if (pct >= 60) return "text-[#C36A2F]";
    return "text-red-500";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="test-review-dialog">
        <DialogHeader>
          <DialogTitle>
            {result?.test_name || "Test Review"}
          </DialogTitle>
          {result && (
            <div className="flex items-center gap-4 mt-2">
              <span className={`text-2xl font-bold ${getScoreColor(result.percentage || 0)}`}>
                {result.percentage?.toFixed(0)}%
              </span>
              <span className="text-sm text-[hsl(var(--muted-foreground))]">
                {result.correct_answers} correct &bull; {result.perfect_cocktails} perfect cocktails
              </span>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                {new Date(result.completed_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-[hsl(var(--muted-foreground))]">Loading review...</p>
        ) : review.length === 0 ? (
          <p className="py-8 text-center text-[hsl(var(--muted-foreground))]">No detailed review available for this test.</p>
        ) : (
          <div className="space-y-2 mt-2" data-testid="review-cocktails">
            {review.map((cocktail, cIdx) => {
              const wrongCount = cocktail.questions.filter(q => !q.is_correct).length;
              const isExpanded = expandedCocktail === cIdx;
              const allCorrect = wrongCount === 0;

              return (
                <div key={cIdx} className="rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                  {/* Cocktail header */}
                  <button
                    onClick={() => setExpandedCocktail(isExpanded ? null : cIdx)}
                    className="w-full flex items-center justify-between p-3 bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors text-left"
                    data-testid={`review-cocktail-${cIdx}`}
                  >
                    <div className="flex items-center gap-2">
                      {cocktail.image_url ? (
                        <img
                          src={`${BACKEND}${cocktail.image_url}`}
                          alt={cocktail.cocktail_name}
                          className="w-8 h-8 rounded object-cover shrink-0"
                        />
                      ) : allCorrect ? (
                        <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                      ) : (
                        <XCircle size={16} className="text-red-500 shrink-0" />
                      )}
                      <span className="font-medium text-sm">{cocktail.cocktail_name}</span>
                      {!cocktail.image_url && null}
                      {cocktail.image_url && (allCorrect ? (
                        <CheckCircle2 size={14} className="text-green-500 shrink-0" />
                      ) : (
                        <XCircle size={14} className="text-red-500 shrink-0" />
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      {wrongCount > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-500/15 text-red-400">
                          {wrongCount} wrong
                        </span>
                      )}
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </button>

                  {/* Questions */}
                  {isExpanded && (
                    <div className="p-3 space-y-2 bg-[hsl(var(--card))]" data-testid={`review-questions-${cIdx}`}>
                      {cocktail.questions.map((q, qIdx) => (
                        <div
                          key={qIdx}
                          className={`p-2.5 rounded-lg text-sm ${
                            q.is_correct
                              ? "bg-green-500/5 border border-green-500/20"
                              : "bg-red-500/5 border border-red-500/20"
                          }`}
                        >
                          <p className="text-[hsl(var(--muted-foreground))] text-xs mb-1.5">{q.question_text}</p>
                          {q.is_correct ? (
                            <div className="flex items-center gap-1.5">
                              <CheckCircle2 size={13} className="text-green-500 shrink-0" />
                              <span className="text-green-400 font-medium">{q.correct_answer}</span>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5">
                                <XCircle size={13} className="text-red-500 shrink-0" />
                                <span className="text-red-400 line-through">{q.user_answer || "No answer"}</span>
                              </div>
                              <div className="flex items-center gap-1.5 pl-5">
                                <span className="text-xs text-[hsl(var(--muted-foreground))]">Correct:</span>
                                <span className="text-green-400 font-medium">{q.correct_answer}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
